import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Globe20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { TEAMS_LANGUAGE_MODE, type LanguagePreference } from '../i18n/languages';

interface LanguageOption {
  value: LanguagePreference;
  labelKey: string;
  /** Two-letter country badge shown before the language name (empty for the Teams option). */
  badge: string;
}

const TEAMS_OPTION: LanguageOption = {
  value: TEAMS_LANGUAGE_MODE,
  labelKey: 'language.useTeams',
  badge: '',
};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  TEAMS_OPTION,
  { value: 'en-US', labelKey: 'language.english', badge: 'US' },
  { value: 'sv-SE', labelKey: 'language.swedish', badge: 'SE' },
  { value: 'fi-FI', labelKey: 'language.finnish', badge: 'FI' },
  { value: 'nb-NO', labelKey: 'language.norwegian', badge: 'NO' },
  { value: 'da-DK', labelKey: 'language.danish', badge: 'DK' },
];

const useStyles = makeStyles({
  trigger: {
    minWidth: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '22px',
    height: '16px',
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: '10px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.02em',
  },
  itemSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
});

export function LanguageSelector() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { preference, setPreference, isLoading } = useLanguage();

  const currentOption =
    LANGUAGE_OPTIONS.find((option) => option.value === preference) ?? TEAMS_OPTION;

  const handleSelect = (value: LanguagePreference) => {
    if (value === preference) {
      return;
    }
    setPreference(value).catch((error) => {
      console.error('Failed to update language preference', error);
    });
  };

  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <Tooltip
          content={t('language.selectTooltip', { language: t(currentOption.labelKey) })}
          relationship="label"
          withArrow
        >
          <Button
            appearance="subtle"
            size="small"
            className={styles.trigger}
            icon={<Globe20Regular />}
            disabled={isLoading}
            data-testid="language-selector"
            aria-label={t('language.selectTooltip', { language: t(currentOption.labelKey) })}
          />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList data-testid="language-selector-control-group">
          {LANGUAGE_OPTIONS.map((option) => {
            const label = t(option.labelKey);
            const tooltip =
              option.value === TEAMS_LANGUAGE_MODE
                ? t('language.useTeamsTooltip')
                : t('language.optionTooltip', { language: label });
            return (
              <MenuItem
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={option.value === preference ? styles.itemSelected : undefined}
                title={tooltip}
                icon={
                  option.badge ? (
                    <span className={styles.badge} aria-hidden="true">
                      {option.badge}
                    </span>
                  ) : (
                    <Globe20Regular />
                  )
                }
              >
                <Text>{label}</Text>
              </MenuItem>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
